import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  company: string;
  avatar?: string;
  role: string;
  createdAt: string;
  onboardingCompleted?: boolean;
}

interface SignupData {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  password: string;
  marketingEmails: boolean;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  signup: (data: SignupData) => Promise<void>;
  logout: () => void;
  googleSignIn: () => Promise<void>;
  refreshToken: () => Promise<void>;
  updateOnboardingStatus: (completed: boolean) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user;

  // Function to decode JWT token and check expiration
  const isTokenExpiring = (token: string, bufferMinutes: number = 30): boolean => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expirationTime = payload.exp * 1000; // Convert to milliseconds
      const bufferTime = bufferMinutes * 60 * 1000; // Convert minutes to milliseconds
      return Date.now() > (expirationTime - bufferTime);
    } catch (error) {
      return true; // If we can't decode, assume it's expiring
    }
  };

  // Automatic token refresh functionality
  const checkAndRefreshToken = async (): Promise<void> => {
    const token = localStorage.getItem('token');
    const refreshTokenValue = localStorage.getItem('refreshToken');
    
    if (!token || !refreshTokenValue) {
      return;
    }

    // Check if token is expiring soon (within 30 minutes)
    if (isTokenExpiring(token, 30)) {
      try {
        console.log('Token expiring soon, refreshing...');
        await refreshToken();
      } catch (error) {
        console.error('Auto-refresh failed:', error);
        // Will be handled by the refreshToken function (logout on failure)
      }
    }
  };

  // Set up periodic token refresh check
  useEffect(() => {
    if (!isAuthenticated) return;

    // Check token every 5 minutes
    const interval = setInterval(checkAndRefreshToken, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  // Check for existing token on app start
  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('token');
      
      if (token) {
        try {
          // Check if token is expired or expiring very soon (within 5 minutes)
          if (isTokenExpiring(token, 5)) {
            const refreshTokenValue = localStorage.getItem('refreshToken');
            if (refreshTokenValue) {
              try {
                console.log('Token expired, attempting refresh on app start...');
                await refreshToken();
                // After refresh, we should have a valid user set, so we can finish loading
                setIsLoading(false);
                return;
              } catch (error) {
                console.error('Failed to refresh token on app start:', error);
                localStorage.removeItem('token');
                localStorage.removeItem('refreshToken');
                setIsLoading(false);
                return;
              }
            } else {
              localStorage.removeItem('token');
              setIsLoading(false);
              return;
            }
          }

          // Verify token and get user data - handle graceful auth responses
          const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001'}/api/auth/verify`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`, // Get fresh token in case it was refreshed
              'Content-Type': 'application/json',
            },
          });
          
          if (response.ok) {
            const data = await response.json();
            if (data.success && data.authenticated) {
              setUser(data.data);
              console.log('✅ User authenticated successfully:', data.data.email);
            } else {
              // Token is invalid or not authenticated - clean up gracefully
              console.log('Token verification failed:', data.message || 'Unknown reason');
              localStorage.removeItem('token');
              localStorage.removeItem('refreshToken');
            }
          } else {
            // Unexpected HTTP error - log it
            console.error('Unexpected error during token verification:', response.status, response.statusText);
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
          }
        } catch (error) {
          // Network errors or other exceptions
          console.error('Token verification failed:', error);
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
        }
      }
      
      // Always set loading to false at the end, regardless of outcome
      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (email: string, password: string, rememberMe: boolean = false): Promise<void> => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001'}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password, rememberMe }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || 'Login failed');
      }

      const data = await response.json();
      
      if (data.success) {
        // Store tokens
        localStorage.setItem('token', data.data.token);
        if (data.data.refreshToken) {
          localStorage.setItem('refreshToken', data.data.refreshToken);
        }
        
        // Set user data
        setUser(data.data.user);
      } else {
        throw new Error(data.message || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const signup = async (data: SignupData): Promise<void> => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001'}/api/auth/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || 'Sign up failed');
      }

      const responseData = await response.json();
      
      if (responseData.success) {
        // Store tokens
        localStorage.setItem('token', responseData.data.token);
        if (responseData.data.refreshToken) {
          localStorage.setItem('refreshToken', responseData.data.refreshToken);
        }
        
        // Set user data
        setUser(responseData.data.user);
      } else {
        throw new Error(responseData.message || 'Sign up failed');
      }
    } catch (error) {
      console.error('Signup error:', error);
      throw error;
    }
  };

  const googleSignIn = async (): Promise<void> => {
    try {
      // For now, simulate Google OAuth
      // In production, this would integrate with Google OAuth API
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001'}/api/auth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          // This would contain the Google OAuth token in production
          googleToken: 'mock-google-token' 
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || 'Google sign-in failed');
      }

      const data = await response.json();
      
      if (data.success) {
        // Store tokens
        localStorage.setItem('token', data.data.token);
        if (data.data.refreshToken) {
          localStorage.setItem('refreshToken', data.data.refreshToken);
        }
        
        // Set user data
        setUser(data.data.user);
      } else {
        throw new Error(data.message || 'Google sign-in failed');
      }
    } catch (error) {
      console.error('Google sign-in error:', error);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    setUser(null);
    
    // Optional: call backend to invalidate token
    fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001'}/api/auth/logout`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
    }).catch(() => {
      // Ignore errors on logout
    });
  };

  const refreshToken = async (): Promise<void> => {
    const refreshTokenValue = localStorage.getItem('refreshToken');
    
    if (!refreshTokenValue) {
      throw new Error('No refresh token available');
    }

    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001'}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken: refreshTokenValue }),
      });

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      const data = await response.json();
      
      if (data.success) {
        console.log('Token refreshed successfully');
        localStorage.setItem('token', data.data.token);
        if (data.data.refreshToken) {
          localStorage.setItem('refreshToken', data.data.refreshToken);
        }
        
        // After refreshing token, verify it to get user data
        try {
          const verifyResponse = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001'}/api/auth/verify`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${data.data.token}`,
              'Content-Type': 'application/json',
            },
          });
          
          if (verifyResponse.ok) {
            const verifyData = await verifyResponse.json();
            if (verifyData.success && verifyData.authenticated) {
              setUser(verifyData.data);
              console.log('✅ User data updated after token refresh');
            }
          }
        } catch (verifyError) {
          console.warn('Failed to verify user after token refresh:', verifyError);
        }
      } else {
        throw new Error('Token refresh failed');
      }
    } catch (error) {
      console.error('Token refresh error:', error);
      // If refresh fails, logout user
      logout();
      throw error;
    }
  };

  const updateOnboardingStatus = (completed: boolean) => {
    setUser(prevUser => {
      if (!prevUser) return null;
      return {
        ...prevUser,
        onboardingCompleted: completed,
      };
    });
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    signup,
    logout,
    googleSignIn,
    refreshToken,
    updateOnboardingStatus,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 